import type { GameStore } from '../store/GameStore';

interface TutorialTip {
  id: string;
  message: string;
  shown: boolean;
}

/**
 * Contextual tutorial system.
 * Shows tips as an assistant dialog when game events/states occur for the first time.
 * Tips queue and show one at a time, auto-dismiss on click or after timeout.
 */
export class TutorialManager {
  private container: HTMLElement;
  private store: GameStore;
  private tips: TutorialTip[];
  private activeEl: HTMLDivElement | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;
  private enabled: boolean = true;
  private checkInterval: ReturnType<typeof setInterval>;

  constructor(container: HTMLElement, store: GameStore) {
    this.container = container;
    this.store = store;

    this.tips = [
      { id: 'welcome', message: "Welcome! I'm Pixel, your assistant. Let's build a game studio!", shown: false },
      { id: 'create_team', message: "First, create a team! Click '+ Team' in the top bar to get started.", shown: false },
      { id: 'start_project', message: "Your team is ready! Click '+ Project' to start making a game.", shown: false },
      { id: 'hire_staff', message: "You can hire more staff by clicking 'Hire'. Better talent = better games!", shown: false },
      { id: 'low_funds', message: "Funds are getting low! Make sure your games earn more than your expenses.", shown: false },
      { id: 'first_release', message: "Your first game is released! Watch the reviews come in...", shown: false },
      { id: 'staff_demand', message: "A staff member has a request. Keep your team happy or they might leave!", shown: false },
      { id: 'level_up', message: "Your company leveled up! New options may be available.", shown: false },
    ];

    // Show welcome tip only on a fresh game (tick 0, no products)
    const initialState = store.getState();
    if (initialState.company.currentTick === 0 && initialState.products.length === 0) {
      setTimeout(() => this.showTip('welcome'), 1500);
    } else {
      // Loading a save — mark all early tips as shown
      for (const tip of this.tips) {
        if (['welcome', 'create_team', 'start_project', 'hire_staff', 'first_release'].includes(tip.id)) {
          tip.shown = true;
        }
      }
    }

    // Subscribe to events
    store.on('STAFF_DEMAND', () => this.showTip('staff_demand'));
    store.on('COMPANY_LEVELED_UP', () => this.showTip('level_up'));
    store.on('LOW_FUNDS_WARNING', () => this.showTip('low_funds'));
    store.on('REVIEW_SCORES_GENERATED', () => {
      // Only show for the very first product
      if (this.store.getState().company.totalProductsReleased === 1) {
        this.showTip('first_release');
      }
    });

    // Periodic state checks for contextual tips
    this.checkInterval = setInterval(() => this.checkState(), 3000);
  }

  private checkState(): void {
    if (!this.enabled) return;
    const state = this.store.getState();

    // After welcome, prompt to create team
    if (this.tips.find((t) => t.id === 'welcome')?.shown && state.teams.length === 0) {
      this.showTip('create_team');
    }

    // After team created, prompt to start project
    if (state.teams.length > 0 && state.teams.some((t) => t.memberIds.length > 0) && state.projects.length === 0) {
      this.showTip('start_project');
    }

    // After first project, prompt to hire
    if (state.projects.length > 0 && state.staff.length === 1 && state.hiringPool.length > 0) {
      this.showTip('hire_staff');
    }
  }

  private showTip(id: string): void {
    if (!this.enabled) return;
    const tip = this.tips.find((t) => t.id === id);
    if (!tip || tip.shown) return;
    tip.shown = true;

    // Don't interrupt an active tip — it'll show on next opportunity
    if (this.activeEl) return;

    this.displayTip(tip.message);
  }

  private displayTip(message: string): void {
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed;
      bottom: 60px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--color-bg-panel);
      border: 2px solid var(--color-xp);
      border-radius: 8px;
      padding: 12px 20px;
      color: var(--color-text-primary);
      font-size: 13px;
      z-index: 150;
      max-width: 400px;
      text-align: center;
      pointer-events: auto;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    `;

    const label = document.createElement('div');
    label.textContent = '💡 Pixel';
    label.style.cssText = 'font-size:10px;color:var(--color-xp);margin-bottom:4px;font-weight:600;';
    el.appendChild(label);

    const text = document.createElement('div');
    text.textContent = message;
    el.appendChild(text);

    const dismiss = document.createElement('div');
    dismiss.textContent = '(click to dismiss)';
    dismiss.style.cssText = 'font-size:9px;color:var(--color-text-muted);margin-top:6px;';
    el.appendChild(dismiss);

    el.addEventListener('click', () => this.dismissActive());

    this.container.appendChild(el);
    this.activeEl = el;

    this.dismissTimer = setTimeout(() => this.dismissActive(), 8000);
  }

  private dismissActive(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.activeEl) {
      this.activeEl.remove();
      this.activeEl = null;
    }
  }

  /** Toggle tutorial tips on/off. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.dismissActive();
  }

  destroy(): void {
    clearInterval(this.checkInterval);
    this.dismissActive();
  }
}
